import React, { useState, useEffect } from 'react';
import { Card, Form, Switch, Select, Button, message, Divider, Space, Typography, InputNumber } from 'antd';
import { RobotOutlined, UnorderedListOutlined, EditOutlined } from '@ant-design/icons';
import api from '../api';
import AIActionPlan from './AIActionPlan';
import AIProblemListGenerator from './AIProblemListGenerator';

const { Option } = Select;
const { Title, Text } = Typography;

const ClinicianReview = ({ patientId, onFlagsSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [patientDemographics, setPatientDemographics] = useState({});
  const [showActionPlan, setShowActionPlan] = useState(false);
  const [showProblemListGenerator, setShowProblemListGenerator] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [flagsResponse, demographicsResponse] = await Promise.all([
          api.get(`/patients/${patientId}/clinical-flags`),
          api.get(`/patients/${patientId}`),
        ]);
        
        const flagsData = flagsResponse.data.data;

        // Handle smoking status for backward compatibility
        const getSmokingStatus = (flags) => {
          if (flags.smoking_status) {
            return flags.smoking_status;
          }
          if (flags.isSmoker) {
            return 'current';
          }
          // Cannot distinguish between former and never, default to never
          return 'never';
        };

        const unifiedFlagsData = {
          ...flagsData,
          smoking_status: getSmokingStatus(flagsData),
        };

        setInitialValues(unifiedFlagsData);
        form.setFieldsValue(unifiedFlagsData);
        setHasUnsavedChanges(false);

        setPatientDemographics(demographicsResponse.data.data.patient);

      } catch (error) {
        message.error('Failed to load clinical data.');
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchData();
    }
  }, [patientId, form]);

  // Reset unsaved changes when component mounts/unmounts to prevent state persistence across tab switches
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts (e.g., when switching tabs)
      setHasUnsavedChanges(false);
    };
  }, []);

  const handleSave = async (values) => {
    setLoading(true);

    // Show loading message
    const hideLoading = message.loading('Saving clinical flags...', 0);

    try {
      // Handle smoking status for backward compatibility
      const updatedValues = {
        ...values,
        isSmoker: values.smoking_status === 'current',
        qf_smoker: values.smoking_status === 'current' || values.smoking_status === 'former',
      };

      await api.patch(`/patients/${patientId}/clinical-flags`, updatedValues);
      hideLoading();
      message.success({
        content: 'Clinical flags saved successfully!',
        duration: 3,
        style: {
          marginTop: '20vh',
        },
      });

      // Update initial values to reflect saved state (including unified smoking field)
      setInitialValues(updatedValues);
      setHasUnsavedChanges(false);
      setIsEditMode(false); // Exit edit mode after successful save

      if (onFlagsSaved) {
        onFlagsSaved();
      }
    } catch (error) {
      hideLoading();
      console.error('Save error:', error);

      const errorMessage = error.response?.data?.message || 'Failed to save clinical flags. Please try again.';
      message.error({
        content: errorMessage,
        duration: 5,
        style: {
          marginTop: '20vh',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit Mode
  const handleEnterEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    // Reset form to initial values first
    form.setFieldsValue(initialValues);
    // Then update states
    setIsEditMode(false);
    setHasUnsavedChanges(false);
  };

  return (
    <Card title="Clinician Review" loading={loading}>
      {/* Edit/Cancel Buttons */}
      {!isEditMode && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<EditOutlined />} onClick={handleEnterEditMode}>
            Update
          </Button>
        </div>
      )}
      {isEditMode && (
        <div style={{ marginBottom: 16 }}>
          <Button onClick={handleCancelEdit}>
            Cancel
          </Button>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={initialValues}
        onValuesChange={() => {
          // Only set hasUnsavedChanges if we're in edit mode
          if (isEditMode) {
            setHasUnsavedChanges(true);
          }
        }}
        disabled={!isEditMode}
      >
        <Form.Item name="smoking_status" label="Smoking Status">
          <Select placeholder="Select smoking status">
            <Option value="never">Never Smoked</Option>
            <Option value="former">Former Smoker</Option>
            <Option value="current">Current Smoker</Option>
          </Select>
        </Form.Item>
        <Form.Item name="isOnBpMeds" label="On BP Meds" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="isOnStatin" label="On Statin" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="hasDiabetes" label="Has Diabetes" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="isPregnant" label="Pregnant" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="isPostMenopausal" label="Postmenopause" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="hasCAD" label="Coronary Artery Disease" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="hasHeartFailure" label="Heart Failure" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="hasAtrialFibrillation" label="Atrial Fibrillation" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="hasHistoryOfStroke" label="History of Stroke" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="hasCOPD" label="COPD" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="hasCKD" label="Chronic Kidney Disease" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="hasHistoryOfCancer" label="History of Cancer" valuePropName="checked">
          <Switch />
        </Form.Item>
        
        <Form.Item name="dmMedication" label="Diabetes Medication">
          <Select placeholder="Select medication status">
            <Option value="no">No Diabetes Medication</Option>
            <Option value="oral">Oral Meds Only</Option>
            <Option value="insulin">Insulin Use</Option>
          </Select>
        </Form.Item>

        <Divider />
        <Title level={5}>Garvan Fracture Risk Factors</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
          These factors are used to calculate fracture risk using the validated Garvan calculator.
        </Text>

        <Form.Item
          name="priorFracturesCount"
          label="Prior Fractures (after age 50)"
          tooltip="Number of fractures the patient has had after age 50. Enter 0 if none."
          initialValue={0}
        >
          <InputNumber
            min={0}
            max={10}
            style={{ width: '100%' }}
            placeholder="Enter number of prior fractures"
          />
        </Form.Item>

        <Form.Item
          name="fallsCount"
          label="Falls in Past 12 Months"
          tooltip="Number of falls the patient has experienced in the past year. Enter 0 if none."
          initialValue={0}
        >
          <InputNumber
            min={0}
            max={10}
            style={{ width: '100%' }}
            placeholder="Enter number of falls"
          />
        </Form.Item>

        <Divider />

        <Form.Item name="athleticismLevel" label="Athleticism Level">
          <Select>
            <Option value="non-athletic">Non-Athletic</Option>
            <Option value="athletic">Athletic</Option>
          </Select>
        </Form.Item>
        {isEditMode && (
          <Form.Item>
            <Space wrap>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  backgroundColor: hasUnsavedChanges ? '#ff4d4f' : undefined,
                  borderColor: hasUnsavedChanges ? '#ff4d4f' : undefined,
                }}
              >
                {hasUnsavedChanges ? 'Save Changes' : 'Save'}
              </Button>
            </Space>
          </Form.Item>
        )}

        {/* AI Action Plan buttons - always visible */}
        <Form.Item>
          <Space wrap>
            <Button
              type="default"
              icon={<RobotOutlined />}
              onClick={() => setShowActionPlan(true)}
              style={{
                backgroundColor: '#722ed1',
                borderColor: '#722ed1',
                color: 'white'
              }}
            >
              Generate CARE360 Plan
            </Button>
            <Button
              type="default"
              icon={<UnorderedListOutlined />}
              onClick={() => setShowProblemListGenerator(true)}
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
                color: 'white'
              }}
            >
              Generate Problem List
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Divider */}
      <Divider style={{ margin: '24px 0' }} />

      {/* AI Action Plan Section */}
      <AIActionPlan
        patientId={patientId}
        visible={showActionPlan}
        onClose={() => setShowActionPlan(false)}
      />

      {/* AI Problem List Generator */}
      <AIProblemListGenerator
        visible={showProblemListGenerator}
        onClose={() => setShowProblemListGenerator(false)}
        patientId={patientId}
        onProblemsImported={(importedProblems) => {
          message.success(`Successfully imported ${importedProblems.length} problems to the patient's problem list`);
          // Optionally trigger a refresh of the problem list if needed
          if (onFlagsSaved) {
            onFlagsSaved(); // This might trigger a refresh of the parent component
          }
        }}
      />
    </Card>
  );
};

export default ClinicianReview;
